import { useMemo } from 'react';

import { useOrdersQuery } from '#/api/queries/orderQueries';
import { filterFalsy } from '#/lib/utils';

type OrderIdsByPrice = {
  buy: {
    [price: string]: string[] | undefined;
  };
  sell: {
    [price: string]: string[] | undefined;
  };
};

/**
 * Sync specifically designed to build PRICE:ID map,
 * which is needed for performant use of Orderbook
 *
 * Note: it only process Limit orders without stops (excluding LPP orders with isLiquidationAcquisitionOnly flag)
 * ```
 *  ExampleUserOrdersMap = {
 *   "1.123": ["id1", "id2"],
 *   "5.234": ["id3"],
 *  }
 * ```
 */
export const useFetchAndSyncCurrentMarketOrderPrices = ({
  currentMarketPair,
}: {
  loggedIn: boolean;
  walletAddress: string;
  currentMarketPair?: string;
}) => {
  const { data: allOrdersData, isSuccess } = useOrdersQuery();

  const marketOrders = useMemo(
    () => allOrdersData?.filter(order => order.market === currentMarketPair),
    [allOrdersData, currentMarketPair]
  );

  const priceMap: OrderIdsByPrice = useMemo(() => {
    if (!marketOrders || !isSuccess) {
      return {
        buy: {},
        sell: {},
      };
    }
    return marketOrders.reduce(
      (map, order) => {
        if (
          !order.price ||
          // LPP orders are excluded from Orderbook
          order.isLiquidationAcquisitionOnly ||
          // Ignore TakeProfit/StopLoss orders
          order.type !== 'limit'
        ) {
          return map;
        }
        map[order.side][order.price] = [
          ...(map[order.side][order.price] || []),
          order.orderId,
        ];
        return map;
      },
      { buy: {}, sell: {} } as OrderIdsByPrice
    );
  }, [isSuccess, marketOrders]);

  const askPriceLevelsArray = useMemo(() => {
    return Object.entries(priceMap.sell)
      .map(([price, orderIds]) => {
        if (orderIds?.length) {
          return price;
        }
        return undefined;
      })
      .filter(filterFalsy);
  }, [priceMap]);

  const bidPriceLevelsArray = useMemo(() => {
    return Object.entries(priceMap.buy)
      .map(([price, orderIds]) => {
        if (orderIds?.length) {
          return price;
        }
        return undefined;
      })
      .filter(filterFalsy);
  }, [priceMap]);

  return {
    askPriceLevelsArray,
    bidPriceLevelsArray,
  };
};
